import type { ReactNode } from "react";
import { FiRotateCw, FiTrash2 } from "react-icons/fi";

export interface TrashTableColumn {
  /** column header text */
  header: string;
  /** CSS class for the column */
  className?: string;
}

interface TrashTableProps<T> {
  /** array of items to display */
  items: T[];
  /** column definitions */
  columns: TrashTableColumn[];
  /** selected item IDs */
  selectedItems: Set<string>;
  /** get unique ID for an item */
  getItemId: (item: T) => string;
  /** render a table row for an item */
  renderRow: (item: T) => ReactNode;
  /** toggle selection for an item */
  onToggleSelect: (id: string) => void;
  /** select/deselect all items */
  onSelectAll: () => void;
  /** restore a single item */
  onRestore: (id: string) => void;
  /** delete a single item */
  onDelete: (id: string) => void;
  /** whether any processing is happening */
  isProcessing?: boolean;
}

/**
 * Generic trash table component
 * Handles table structure, selection, and actions
 * Content is customized via renderRow prop
 */
export function TrashTable<T>({
  items,
  columns,
  selectedItems,
  getItemId,
  renderRow,
  onToggleSelect,
  onSelectAll,
  onRestore,
  onDelete,
  isProcessing = false,
}: TrashTableProps<T>) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {/* Select All Checkbox */}
            <th scope="col" className="w-12 px-6 py-3">
              <input
                type="checkbox"
                checked={selectedItems.size === items.length && items.length > 0}
                onChange={onSelectAll}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            </th>

            {/* Column Headers */}
            {columns.map((column) => (
              <th
                key={column.header}
                scope="col"
                className={
                  column.className ||
                  "px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                }
              >
                {column.header}
              </th>
            ))}

            {/* Actions Column */}
            <th scope="col" className="relative px-6 py-3">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-200 bg-white">
          {items.map((item) => {
            const id = getItemId(item);
            const isSelected = selectedItems.has(id);

            return (
              <tr
                key={id}
                className={`hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""}`}
              >
                {/* Select Checkbox */}
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect(id)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </td>

                {/* Custom Row Content */}
                {renderRow(item)}

                {/* Action Buttons */}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    type="button"
                    onClick={() => onRestore(id)}
                    disabled={isProcessing}
                    className="mr-3 text-blue-600 hover:text-blue-900 disabled:opacity-50"
                  >
                    <FiRotateCw className="inline h-4 w-4" /> Restore
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(id)}
                    disabled={isProcessing}
                    className="text-red-600 hover:text-red-900 disabled:opacity-50"
                  >
                    <FiTrash2 className="inline h-4 w-4" /> Delete
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
